/**
 * `/settings/firm` — US-003 firm branding + preferences (T-1.19). Renders under the
 * `_settings` layout (managing_partner | firm_admin reach it; other roles are
 * redirected by the layout guard — §9).
 *
 * The SAVE / edit affordance is gated on `firm.write` (managing_partner only) via the
 * ENG-10 generated catalogue (CC-2 — no permission string literal here; `usePermission`
 * + `<Can>` read the catalogue). A firm_admin SEES the current branding but every input
 * is read-only and there is no Save button — they cannot mutate it. Saves persist via
 * the T-1.14 endpoints and invalidate the shell's branding query so the app shell
 * reflects the change.
 *
 * Built on the design-system primitives (UI/UX Flow 28). The logo is checked client-side
 * (type + size) before any request. F-29: the API returns a same-origin, session-gated
 * `logo_url` (`/api/v1/firms/logo?v=…` — root-relative like every other API href, e.g.
 * `reportDownloadHref`), so the card shows a real preview; a successful upload invalidates
 * the settings query, and the cache-busting `v` makes the new image appear at once.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Image as ImageIcon, Save } from '../../components/ui/icons.ts';
import { useEffect, useId, useState, type ChangeEvent, type FormEvent, type JSX } from 'react';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  ErrorState,
  FieldFrame,
  Input,
  Notice,
  PageHeader,
  Skeleton,
  SkeletonText,
} from '../../components/ui/index.ts';
import { api, getErrorMessage, type FirmSettings } from '../../lib/api.ts';
import { useAuth } from '../../store/auth-store.ts';
import { getRequestId } from '../../lib/request-id.ts';
import { Can, usePermission } from '../../lib/permissions/index.tsx';

import '../../styles/settings.css';

const FIRM_SETTINGS_KEY = ['firm', 'settings'] as const;
const DEFAULT_PRIMARY = '#2f6feb';
const DEFAULT_SECONDARY = '#8fb3ff';

/** The logo types the server stores; checked here first so a wrong file never leaves the browser. */
const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml'] as const;
const LOGO_ACCEPT = LOGO_TYPES.join(',');
const LOGO_MAX_BYTES = 1024 * 1024;
const HEX_COLOUR = /^#[0-9a-f]{6}$/i;

/** Client-side logo checks — a message to show inline, or null when the file is acceptable. */
function logoProblem(file: File): string | null {
  if (!(LOGO_TYPES as readonly string[]).includes(file.type)) {
    return 'Use a PNG, SVG or JPEG image.';
  }
  if (file.size > LOGO_MAX_BYTES) {
    return 'The logo must be 1 MB or smaller.';
  }
  return null;
}

/**
 * One brand colour: the native picker beside a mono text field showing the hex, both wired
 * to the same value. Read-only users get a `readOnly` text field (still in the tab order)
 * beside a static swatch (F-32): the picker has no read-only mode in HTML and a `disabled`
 * control would drop out of the tab order, so it is simply not rendered for them and the
 * label points at the hex field instead.
 */
function ColourField({
  label,
  name,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (next: string) => void;
  readOnly: boolean;
}): JSX.Element {
  const pickerId = useId();
  // The text the user is typing; null while it mirrors the committed value.
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? value;
  const draftInvalid = draft !== null && !HEX_COLOUR.test(draft);

  function onHexChange(e: ChangeEvent<HTMLInputElement>): void {
    const next = e.target.value.trim();
    if (HEX_COLOUR.test(next)) {
      onChange(next.toLowerCase());
      setDraft(null);
    } else {
      setDraft(next);
    }
  }

  return (
    <FieldFrame id={pickerId} label={label}>
      <div className="argus-settings-colour">
        {readOnly ? (
          <span
            className="argus-settings-swatch argus-settings-swatch--static"
            style={{ background: value }}
            aria-hidden="true"
          />
        ) : (
          <input
            id={pickerId}
            type="color"
            name={name}
            className="argus-settings-swatch"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
        <Input
          {...(readOnly ? { id: pickerId } : {})}
          label={`${label} hex`}
          hideLabel
          mono
          value={shown}
          onChange={onHexChange}
          onBlur={() => setDraft(null)}
          readOnly={readOnly}
          spellCheck={false}
          autoComplete="off"
          maxLength={7}
          error={draftInvalid ? 'Use a 6-digit hex colour, e.g. #355cff.' : undefined}
        />
      </div>
    </FieldFrame>
  );
}

export function FirmSettingsScreen(): JSX.Element {
  const queryClient = useQueryClient();
  const canWrite = usePermission('firm.write');

  const settings = useQuery<FirmSettings>({
    queryKey: FIRM_SETTINGS_KEY,
    queryFn: () => api.firms.getSettings(),
  });
  const data = settings.data;

  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);
  const [secondary, setSecondary] = useState(DEFAULT_SECONDARY);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logoError, setLogoError] = useState<string | null>(null);

  // Seed the editable fields once the current settings load.
  useEffect(() => {
    if (data) {
      setPrimary(data.primary_colour ?? DEFAULT_PRIMARY);
      setSecondary(data.secondary_colour ?? DEFAULT_SECONDARY);
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body: FirmSettings) => api.firms.updateSettings(body),
    onSuccess: async (saved) => {
      useAuth.getState().setFirmLogo(saved.logo_url ?? null);
      setStatus('Branding saved.');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: FIRM_SETTINGS_KEY });
    },
    onError: (err) => {
      setError(getErrorMessage(err));
      setStatus(null);
    },
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => api.firms.uploadLogo(file, file.type),
    onSuccess: async (uploaded) => {
      if (uploaded.logo_url !== undefined) useAuth.getState().setFirmLogo(uploaded.logo_url);
      setStatus('Logo uploaded.');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: FIRM_SETTINGS_KEY });
    },
    onError: (err) => {
      setError(getErrorMessage(err));
      setStatus(null);
    },
  });

  function handleSave(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setStatus(null);
    setError(null);
    saveMutation.mutate({ primary_colour: primary, secondary_colour: secondary });
  }

  function handleLogo(e: ChangeEvent<HTMLInputElement>): void {
    const file = e.target.files?.[0];
    if (!file) return;
    const problem = logoProblem(file);
    setLogoError(problem);
    if (problem) return;
    setStatus(null);
    setError(null);
    logoMutation.mutate(file);
  }

  const description = 'Your firm logo and colours apply across ARGUS — in the app and on reports.';

  if (settings.isPending) {
    return (
      <div className="argus-page">
        <PageHeader title={<Skeleton width={280} height={28} />} description={description} />
        <Card>
          <CardHeader title="Branding" />
          <CardBody>
            <SkeletonText lines={3} label="Loading firm settings" />
          </CardBody>
        </Card>
      </div>
    );
  }

  if (settings.isError) {
    return (
      <div className="argus-page">
        <PageHeader title="Firm settings & branding" description={description} />
        <ErrorState
          title="We couldn't load the firm settings"
          message={getErrorMessage(settings.error)}
          requestId={getRequestId(settings.error)}
          onRetry={() => void settings.refetch()}
          retrying={settings.isFetching}
        />
      </div>
    );
  }

  return (
    <div className="argus-page">
      <PageHeader title="Firm settings & branding" description={description} />

      {!canWrite ? (
        <Notice role="note">
          You can view your firm branding. Only a managing partner can change it.
        </Notice>
      ) : null}
      {status ? <Notice tone="success">{status}</Notice> : null}
      {error ? <ErrorState title="The change was not saved" message={error} /> : null}

      <form onSubmit={handleSave} aria-label="Firm branding" className="argus-settings-form">
        <Card>
          <CardHeader
            title="Branding"
            description="The primary colour is used for accents and actions; the secondary for highlights."
          />
          <CardBody>
            <div className="stack">
              <ColourField
                label="Primary colour"
                name="primary_colour"
                value={primary}
                onChange={setPrimary}
                readOnly={!canWrite}
              />
              <ColourField
                label="Secondary colour"
                name="secondary_colour"
                value={secondary}
                onChange={setSecondary}
                readOnly={!canWrite}
              />
            </div>
          </CardBody>
          <Can permission="firm.write">
            <CardFooter>
              <Button
                type="submit"
                variant="primary"
                loading={saveMutation.isPending}
                icon={<Save size={16} aria-hidden="true" />}
              >
                Save branding
              </Button>
            </CardFooter>
          </Can>
        </Card>
      </form>

      <Card className="argus-settings-form">
        <CardHeader
          title="Logo"
          description="Shown in the app shell and on generated reports. PNG, SVG or JPEG, up to 1 MB."
        />
        <CardBody>
          <div className="stack">
            {settings.data.logo_url ? (
              <div className="argus-settings-logo">
                <img
                  className="argus-settings-logo__preview"
                  src={settings.data.logo_url}
                  alt="Current firm logo"
                />
                <div className="stack-sm">
                  <Badge tone="success">Logo set</Badge>
                  {settings.data.logo_r2_key ? (
                    <span className="argus-settings-logo__key">{settings.data.logo_r2_key}</span>
                  ) : null}
                </div>
              </div>
            ) : (
              <div className="argus-settings-logo">
                <span
                  className="argus-settings-logo__preview argus-settings-logo__preview--empty"
                  aria-hidden="true"
                >
                  <ImageIcon size={22} />
                </span>
                <p className="text-muted text-sm">No logo uploaded yet.</p>
              </div>
            )}
            <Can permission="firm.write">
              <Input
                type="file"
                label="Upload logo"
                className="argus-settings-file"
                accept={LOGO_ACCEPT}
                onChange={handleLogo}
                disabled={logoMutation.isPending}
                error={logoError}
                help={
                  logoMutation.isPending ? 'Uploading…' : 'Replaces the current logo immediately.'
                }
              />
            </Can>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
